import {
  ChefHat,
  Users,
  ShoppingCart,
  Calendar,
  TrendingUp,
  Activity,
  Clock,
  Plus,
  AlertTriangle,
  PackageOpen,
  ArrowDownCircle,
  ArrowUpCircle,
  Sliders,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDashboardStats } from '../hooks/useDashboardStats';
import PageHeader from '../components/PageHeader';
import { etiquetaMovimiento } from '@suminuta/shared';
import { useExistencias } from '../hooks/useReportes';
import type { NivelFrase } from '@suminuta/shared';
import {
  fechaLocalConOffset,
  obtenerFechaActualLocal,
} from '../utils/dateUtils';

type StatCard = {
  name: string;
  value: number;
  icon: typeof ChefHat;
  color: string;
  bgColor: string;
  borderColor: string;
  onClick?: () => void;
  highlight?: boolean;
  /** Subtítulo de una línea: la frase más importante de la lectura (N°056). */
  sub?: string;
  subNivel?: NivelFrase;
};

const formatRelativo = (iso: string): string => {
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return 'hace unos segundos';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    const dias = Math.floor(diff / 86400);
    return dias === 1 ? 'hace 1 día' : `hace ${dias} días`;
  } catch {
    return '';
  }
};

const ESTADO_PEDIDO_META: Record<
  string,
  { label: string; tone: string; bg: string; border: string }
> = {
  borrador: {
    label: 'Borrador',
    tone: 'text-gray-700 dark:text-gray-300',
    bg: 'bg-gray-50 dark:bg-gray-700/30',
    border: 'border-gray-200 dark:border-gray-600',
  },
  reservado: {
    label: 'Reservado',
    tone: 'text-slate-700 dark:text-slate-300',
    bg: 'bg-slate-50 dark:bg-slate-800/40',
    border: 'border-slate-200 dark:border-slate-600',
  },
  solicitado: {
    label: 'Solicitado',
    tone: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
  confirmado: {
    label: 'Confirmado',
    tone: 'text-yellow-700 dark:text-yellow-300',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-200 dark:border-yellow-800',
  },
  en_preparacion: {
    label: 'En preparación',
    tone: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  listo: {
    label: 'Listo',
    tone: 'text-indigo-700 dark:text-indigo-300',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    border: 'border-indigo-200 dark:border-indigo-800',
  },
  despachado: {
    label: 'Despachado',
    tone: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
  },
  cancelado: {
    label: 'Cancelado',
    tone: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
  },
  otro: {
    label: 'Otro',
    tone: 'text-gray-700 dark:text-gray-300',
    bg: 'bg-gray-50 dark:bg-gray-700/30',
    border: 'border-gray-200 dark:border-gray-600',
  },
};

const ESTADOS_PEDIDO_ORDER = [
  'borrador',
  'reservado',
  'solicitado',
  'confirmado',
  'en_preparacion',
  'listo',
  'despachado',
  'cancelado',
];

/**
 * Estilo visual por FAMILIA de movimiento.
 *
 * Antes este mapa estaba indexado por `tipo` (entrada/salida/ajuste) y
 * de ahí salía también la etiqueta: una salida de bodega aparecía como
 * "Salida por producción" y un reverso como "Entrada de stock"
 * (PROMPT_N°053). El texto ahora sale de `etiquetaMovimiento(motivo)`,
 * en `@suminuta/shared`, y acá queda sólo el color.
 */
const TONO_MOV: Record<
  string,
  { Icon: typeof ArrowDownCircle; tone: string; bg: string; border: string }
> = {
  compra: {
    Icon: ArrowDownCircle,
    tone: 'text-green-700 dark:text-green-300',
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
  },
  bodega: {
    Icon: ArrowDownCircle,
    tone: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  produccion: {
    Icon: ArrowUpCircle,
    tone: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  reverso: {
    Icon: ArrowDownCircle,
    tone: 'text-teal-700 dark:text-teal-300',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    border: 'border-teal-200 dark:border-teal-800',
  },
  merma: {
    Icon: AlertTriangle,
    tone: 'text-orange-700 dark:text-orange-300',
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
  },
  ajuste: {
    Icon: Sliders,
    tone: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: stats, isLoading, error: loadError, dataUpdatedAt } = useDashboardStats();

  const kpis = stats?.kpis;
  const stockBajo = stats?.stock_bajo ?? [];
  const actividad = stats?.actividad_reciente ?? [];

  // Sólo para el subtítulo: la lectura ya viene calculada con el reporte,
  // así que no hay reglas de negocio duplicadas acá.
  const { data: existencias } = useExistencias({});
  const fraseDestacada =
    existencias?.lectura?.frases?.find(
      (f) => f.nivel === 'alerta' || f.nivel === 'atencion'
    ) ?? existencias?.lectura?.frases?.[0];

  const statCards: StatCard[] = [
    {
      name: 'Recetas activas',
      value: kpis?.recetas_activas ?? 0,
      icon: ChefHat,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      onClick: () => navigate('/recetas'),
    },
    {
      name: 'Clientes activos',
      value: kpis?.clientes_activos ?? 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      onClick: () => navigate('/clientes'),
    },
    {
      name: 'Pedidos pendientes',
      value: kpis?.pedidos_pendientes ?? 0,
      icon: ShoppingCart,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      onClick: () => navigate('/pedidos'),
    },
    {
      name: 'Minutas activas',
      value: kpis?.minutas_activas ?? 0,
      icon: Calendar,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      onClick: () => navigate('/minutas-nuevas'),
    },
    {
      name: 'Ingredientes con stock bajo',
      value: kpis?.ingredientes_stock_bajo ?? 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      // N°056: el subtítulo es la frase más importante de la lectura de
      // Existencias. La tarjeta pasa de decir un número a decir qué pasa, y
      // el clic lleva al reporte que lo explica.
      sub: fraseDestacada?.texto,
      subNivel: fraseDestacada?.nivel,
      onClick: () => navigate('/reportes/existencias'),
      highlight: (kpis?.ingredientes_stock_bajo ?? 0) > 0,
    },
    {
      name: 'Órdenes de compra abiertas',
      value: kpis?.ordenes_compra_abiertas ?? 0,
      icon: PackageOpen,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      borderColor: 'border-indigo-200',
      onClick: () => navigate('/compras'),
    },
    {
      name: 'Movimientos últimos 30 días',
      value: kpis?.movimientos_ultimos_30d ?? 0,
      icon: Activity,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50',
      borderColor: 'border-cyan-200',
      // Al kardex con el rango ya cargado, no a /reportes: la tarjeta
      // cuenta movimientos y ahora hay una pantalla que los muestra
      // (PROMPT_N°053). El rango se arma en hora local.
      onClick: () =>
        navigate(
          `/movimientos?desde=${fechaLocalConOffset(-29)}&hasta=${obtenerFechaActualLocal()}`
        ),
    },
  ];

  return (
    <div
      className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-all duration-300 relative"
      style={{
        backgroundImage: "url('/bg_dash.webp')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-white/85 dark:bg-gray-900/85 transition-colors duration-300"></div>

      <div className="relative z-10">
        <PageHeader title="Dashboard" helpSeccion="dashboard" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome */}
          <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6 bg-gradient-to-r from-blue-50/50 to-purple-50/50 dark:from-blue-900/20 dark:to-purple-900/20">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                  Bienvenido, {user?.nombre} 👋
                </h2>
                <p className="text-gray-600 dark:text-gray-300 text-lg">
                  Sistema de gestión de minutas y recetas para servicios de
                  alimentación
                </p>
                <div className="flex items-center mt-3 text-sm text-gray-500 dark:text-gray-400">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>
                    {isLoading
                      ? 'Cargando estadísticas…'
                      : dataUpdatedAt
                        ? `Actualizado ${formatRelativo(new Date(dataUpdatedAt).toISOString())}`
                        : 'Sin datos'}
                  </span>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-4 rounded-2xl">
                  <Activity className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          {loadError ? (
            <div className="mb-6 rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300">
              No pudimos cargar las estadísticas. Refresca la página en unos
              minutos o contacta a soporte si el problema persiste.
            </div>
          ) : null}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              const Wrapper: any = stat.onClick ? 'button' : 'div';
              return (
                <Wrapper
                  key={stat.name}
                  onClick={stat.onClick}
                  className={`text-left w-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border ${stat.borderColor} dark:border-gray-700 p-6 transition-all duration-200 ${
                    stat.onClick
                      ? 'hover:shadow-md dark:hover:shadow-xl hover:-translate-y-0.5 cursor-pointer'
                      : ''
                  } ${stat.highlight ? 'ring-2 ring-red-300 dark:ring-red-700' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className={`p-3 rounded-xl ${stat.bgColor} ring-2 ring-white dark:ring-gray-800 shadow-sm`}
                    >
                      <Icon className={`h-6 w-6 ${stat.color}`} />
                    </div>
                    {stat.onClick && (
                      <TrendingUp className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                      {stat.name}
                    </p>
                    <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                      {isLoading ? (
                        <span className="inline-block w-10 h-7 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></span>
                      ) : (
                        stat.value.toLocaleString('es-CL')
                      )}
                    </p>
                    {stat.sub && (
                      <p
                        className={`mt-1 text-xs line-clamp-2 ${
                          stat.subNivel === 'alerta'
                            ? 'text-red-600 dark:text-red-400'
                            : stat.subNivel === 'atencion'
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {stat.sub}
                      </p>
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>

          {/* Pedidos por estado */}
          {stats && Object.keys(stats.pedidos_por_estado).length > 0 && (
            <div className="mb-8 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Pedidos por estado
                </h3>
                <button
                  onClick={() => navigate('/pedidos')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  Ver todos →
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {ESTADOS_PEDIDO_ORDER.filter(
                  (key) => (stats.pedidos_por_estado[key] ?? 0) > 0
                ).map((key) => {
                  const meta = ESTADO_PEDIDO_META[key] ?? ESTADO_PEDIDO_META['otro'];
                  const count = stats.pedidos_por_estado[key] ?? 0;
                  return (
                    <button
                      key={key}
                      onClick={() => navigate('/pedidos')}
                      className={`text-left rounded-xl p-4 border transition-all hover:-translate-y-0.5 hover:shadow-md ${meta.bg} ${meta.border}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span
                          className={`inline-flex items-center text-xs font-semibold uppercase tracking-wide ${meta.tone}`}
                        >
                          {meta.label}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {count.toLocaleString('es-CL')}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Grid inferior */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Accesos Rápidos */}
            <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Accesos rápidos
                </h3>
                <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                  <Plus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/recetas')}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-4 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-left font-medium shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                >
                  <div className="flex items-center">
                    <ChefHat className="h-5 w-5 mr-3" />
                    Ir a recetas
                  </div>
                </button>
                <button
                  onClick={() => navigate('/minutas-nuevas')}
                  className="w-full bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-xl transition-all duration-200 text-left font-medium border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm"
                >
                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 mr-3" />
                    Minutas semanales
                  </div>
                </button>
                <button
                  onClick={() => navigate('/pedidos')}
                  className="w-full bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-xl transition-all duration-200 text-left font-medium border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm"
                >
                  <div className="flex items-center">
                    <ShoppingCart className="h-5 w-5 mr-3" />
                    Gestión de pedidos
                  </div>
                </button>
                <button
                  onClick={() => navigate('/compras')}
                  className="w-full bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-xl transition-all duration-200 text-left font-medium border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm"
                >
                  <div className="flex items-center">
                    <PackageOpen className="h-5 w-5 mr-3" />
                    Compras y proveedores
                  </div>
                </button>
                <button
                  onClick={() => navigate('/ingredientes')}
                  className="w-full bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 py-3 px-4 rounded-xl transition-all duration-200 text-left font-medium border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm"
                >
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 mr-3" />
                    Inventario de ingredientes
                  </div>
                </button>
              </div>
            </div>

            {/* Actividad Reciente */}
            <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Actividad reciente
                </h3>
                <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-lg">
                  <Activity className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
              </div>

              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-14 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse"
                    />
                  ))}
                </div>
              ) : actividad.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                  Aún no hay movimientos registrados en el sistema.
                </p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {actividad.map((item) => {
                    // La etiqueta sale del MOTIVO; el `tipo` sólo decide
                    // el signo. Ver TONO_MOV arriba.
                    const et = etiquetaMovimiento(
                      (item as { motivo?: string }).motivo ?? item.tipo
                    );
                    const meta = TONO_MOV[et.tono] ?? TONO_MOV.ajuste;
                    const Icon = meta.Icon;
                    const signo =
                      item.tipo === 'entrada'
                        ? '+'
                        : item.tipo === 'salida'
                          ? '−'
                          : '';
                    const detalle = (item as { notas?: string | null }).notas;
                    return (
                      <div
                        key={item.id}
                        className={`flex items-start space-x-3 p-3 rounded-xl border ${meta.bg} ${meta.border}`}
                      >
                        <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${meta.tone}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {et.label}: {item.ingrediente}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                            {signo}
                            {Math.abs(Number(item.cantidad))} {item.unidad} · por{' '}
                            {item.usuario}
                            {' · '}
                            {formatRelativo(item.fecha)}
                          </p>
                          {detalle && (
                            <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                              {detalle}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Stock Bajo */}
            <div className="lg:col-span-1 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  Stock bajo
                </h3>
                <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>

              {isLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-14 rounded-xl bg-gray-100 dark:bg-gray-700 animate-pulse"
                    />
                  ))}
                </div>
              ) : stockBajo.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8 text-center">
                  Todos los ingredientes están por encima del stock mínimo 👌
                </p>
              ) : (
                <div className="space-y-3">
                  {stockBajo.map((ing) => {
                    const pct = ing.stock_minimo > 0
                      ? Math.min(100, Math.round((ing.stock_actual / ing.stock_minimo) * 100))
                      : 0;
                    const barColor =
                      pct <= 25 ? 'bg-red-500' : pct <= 60 ? 'bg-amber-500' : 'bg-yellow-500';
                    return (
                      <button
                        key={ing.id}
                        onClick={() => navigate('/ingredientes')}
                        className="w-full text-left p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700 bg-gray-50 dark:bg-gray-700/50 hover:bg-white dark:hover:bg-gray-700 transition-all"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {ing.nombre}
                          </p>
                          <span className="text-xs font-semibold text-red-600 dark:text-red-400">
                            {pct}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden mb-1">
                          <div
                            className={`h-full ${barColor} transition-all`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {ing.stock_actual} / {ing.stock_minimo} {ing.unidad_medida}
                        </p>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

        </main>
      </div>
    </div>
  );
};

export default Dashboard;
